import {
  LimitConfigurationInput,
  LimitGlobalActivation,
  LimitGlobalActivationInTime,
  LimitGlobalAwardCount,
  LimitGlobalAwardCountInTime,
  LimitInTransactionAwardCount,
  LimitInTransactionProgress,
  LimitMembershipActivation,
  LimitMembershipActivationInTime,
  LimitMembershipAwardCount,
  LimitMembershipAwardCountInTime,
  LimitMembershipProgressInTime,
  LimitMembershipTransactionCount,
  LimitMembershipTransactionCountInTime,
  OfferFragment,
} from '@occc/graphql';
import { limitHelper } from '@occc-app/offerHelpers/utils/limitHelper';

export class LimitDuplicationService {
  private static throwNotSupportedLimitError(_limit: never): never {
    throw new Error('Not supported limit');
  }

  public copy(limits: OfferFragment['limits']) {
    const inputs = limits.map((limit) => {
      if (limitHelper.isGlobalActivation(limit)) {
        return this.copyGlobalActivation(limit);
      }
      if (limitHelper.isGlobalActivationInTime(limit)) {
        return this.copyGlobalActivationInTime(limit);
      }
      if (limitHelper.isGlobalAwardCount(limit)) {
        return this.copyGlobalAwardCount(limit);
      }
      if (limitHelper.isGlobalAwardCountInTime(limit)) {
        return this.copyGlobalAwardCountInTime(limit);
      }
      if (limitHelper.isMembershipActivation(limit)) {
        return this.copyMembershipActivation(limit);
      }
      if (limitHelper.isMembershipActivationInTime(limit)) {
        return this.copyMembershipActivationInTime(limit);
      }
      if (limitHelper.isMembershipAwardCount(limit)) {
        return this.copyMembershipAwardCount(limit);
      }
      if (limitHelper.isMembershipAwardCountInTime(limit)) {
        return this.copyMembershipAwardCountInTime(limit);
      }
      if (limitHelper.isInTransactionAwardCount(limit)) {
        return this.copyInTransactionAwardCount(limit);
      }
      if (limitHelper.isInTransactionProgress(limit)) {
        return this.copyInTransactionProgress(limit);
      }
      if (limitHelper.isMembershipProgressInTime(limit)) {
        return this.copyMembershipProgressInTime(limit);
      }
      if (limitHelper.isMembershipTransactionCount(limit)) {
        return this.copyMembershipTransactionCount(limit);
      }
      if (limitHelper.isMembershipTransactionCountInTime(limit)) {
        return this.copyMembershipTransactionCountInTime(limit);
      }

      // Assert that we've handled all Limit types.
      // If a new type is added, TypeScript will flag this as an error.
      return LimitDuplicationService.throwNotSupportedLimitError(limit);
    });

    return inputs;
  }

  private copyGlobalActivation(limit: LimitGlobalActivation): LimitConfigurationInput {
    return {
      globalActivation: {
        limit: limit.limit,
      },
    };
  }

  private copyGlobalActivationInTime(limit: LimitGlobalActivationInTime): LimitConfigurationInput {
    return {
      globalActivationInTime: {
        limit: limit.limit,
        timePeriod: limit.timePeriod,
      },
    };
  }

  private copyGlobalAwardCount(limit: LimitGlobalAwardCount): LimitConfigurationInput {
    return {
      globalAwardCount: {
        limit: limit.limit,
      },
    };
  }

  private copyGlobalAwardCountInTime(limit: LimitGlobalAwardCountInTime): LimitConfigurationInput {
    return {
      globalAwardCountInTime: {
        limit: limit.limit,
        timePeriod: limit.timePeriod,
      },
    };
  }

  private copyMembershipActivation(limit: LimitMembershipActivation): LimitConfigurationInput {
    return {
      membershipActivation: {
        limit: limit.limit,
      },
    };
  }

  private copyMembershipActivationInTime(
    limit: LimitMembershipActivationInTime,
  ): LimitConfigurationInput {
    return {
      membershipActivationInTime: {
        limit: limit.limit,
        timePeriod: limit.timePeriod,
      },
    };
  }

  private copyMembershipAwardCount(limit: LimitMembershipAwardCount): LimitConfigurationInput {
    return {
      membershipAwardCount: {
        limit: limit.limit,
      },
    };
  }

  private copyMembershipAwardCountInTime(
    limit: LimitMembershipAwardCountInTime,
  ): LimitConfigurationInput {
    return {
      membershipAwardCountInTime: {
        limit: limit.limit,
        timePeriod: limit.timePeriod,
      },
    };
  }

  private copyInTransactionAwardCount(
    limit: LimitInTransactionAwardCount,
  ): LimitConfigurationInput {
    return {
      inTransactionAwardCount: {
        limit: limit.limit,
      },
    };
  }

  private copyInTransactionProgress(limit: LimitInTransactionProgress): LimitConfigurationInput {
    return {
      inTransactionProgress: {
        limit: limit.limit,
      },
    };
  }

  private copyMembershipProgressInTime(
    limit: LimitMembershipProgressInTime,
  ): LimitConfigurationInput {
    return {
      membershipProgressInTime: {
        limit: limit.limit,
        timePeriod: limit.timePeriod,
      },
    };
  }

  private copyMembershipTransactionCount(
    limit: LimitMembershipTransactionCount,
  ): LimitConfigurationInput {
    return {
      membershipTransactionCount: {
        limit: limit.limit,
      },
    };
  }

  private copyMembershipTransactionCountInTime(
    limit: LimitMembershipTransactionCountInTime,
  ): LimitConfigurationInput {
    return {
      membershipTransactionCountInTime: {
        limit: limit.limit,
        timePeriod: limit.timePeriod,
      },
    };
  }
}

export const limitDuplicationService = new LimitDuplicationService();
